






























































































import { Component, Vue, Prop } from 'vue-property-decorator';
import { Tooltip } from 'element-ui';
import { utcToLocal } from '@/utilities/dateCast.ts';
import Milestone from '@/milestone/Milestone.vue';
import { getMilestoneType } from '@/utilities/textUtils.ts';

// types
import { ReservationStatusClasses } from '@/constants/reservationStatuses';
import {
  MilestoneFields,
  MilestoneTypeEnum,
  MilestoneFields_details,
  MilestoneFields_devanningDetails,
  MilestoneDoneDateEnum,
  MilestoneFields_doneDates
} from '@/types/graphql/schemaTypes';

import { milestoneService } from '@/services/MilestoneService';
import get from 'lodash/get';

interface FormatedMilestone extends MilestoneFields {
  ids: string[];
  stack: string[];
  eventName: MilestoneDoneDateEnum;
  doneAt: Date | null;
  type: string;
  reservationClass?: string;
}

@Component({
  components: {
    'el-tooltip': Tooltip
  }
})
export default class Milestones extends Vue {
  @Prop({ default: () => [], type: Array })
  private milestones!: MilestoneFields[];

  private formatedMilestones: FormatedMilestone[] = [];

  // goupe of milestone
  private dateTimeTypes = [
    MilestoneTypeEnum.delivery,
    MilestoneTypeEnum.pickup
  ];

  private transportType = [
    MilestoneTypeEnum.air_transportation,
    MilestoneTypeEnum.sea_transportation
  ];

  private readonly drayageTypes = [
    MilestoneTypeEnum.delivery,
    MilestoneTypeEnum.devanning
  ];

  created() {
    this.formatMiletones(this.milestones);
  }

  /**
   * @return true if it's a devanning milestone with deliveryPlaces
   * otherwise it's displayed as normal milestone
   **/
  private isDevanning(milestone: MilestoneFields) {
    return (
      milestone.milestoneType === 'devanning' &&
      milestone.devanningDetails?.container?.deliveryPlaces?.length
    );
  }

  private milestoneStatusClass(
    className: string,
    complete: string | null,
    deadLine: string
  ) {
    if (complete) {
      return className + '-done';
    }
    if (!deadLine) {
      return;
    }
    if (new Date(utcToLocal(deadLine)) < new Date()) {
      return className + '-late';
    }
  }

  private formatTransportation(
    formatedMilestones: FormatedMilestone[],
    milestone: MilestoneFields
  ) {
    const i18nKey = 'shipment.milestone.short-milestone-type';
    // departure
    formatedMilestones.push({
      ...milestone,
      ids: [milestone.id],
      eventDate: milestone.estimatedDateOfDeparture,
      eventName: MilestoneDoneDateEnum.etdDoneAt,
      stack: [milestone.eventDate],
      doneAt: this.findMilestoneDoneAt(
        milestone,
        MilestoneDoneDateEnum.etdDoneAt
      ),
      type: this.$i18n.t(`${i18nKey}.${milestone.milestoneType}`) + ' ETD'
    });
    // arrival
    const formatedEtaMilestone = {
      ...milestone,
      ids: [milestone.id],
      eventDate: milestone.estimatedDateOfArrival,
      eventName: MilestoneDoneDateEnum.etaDoneAt,
      stack: [milestone.eventDate],
      doneAt: this.findMilestoneDoneAt(
        milestone,
        MilestoneDoneDateEnum.etaDoneAt
      ),
      type: this.$i18n.t(`${i18nKey}.${milestone.milestoneType}`) + ' ETA'
    };

    formatedMilestones.push(formatedEtaMilestone);

    return formatedEtaMilestone;
  }

  private formatMiletones(milestones: MilestoneFields[]) {
    let prevMilestone: FormatedMilestone | null = null;
    // loop over milestones
    milestones.forEach((milestone, index) => {
      // split air and sea milestone
      if (this.transportType.includes(milestone.milestoneType!)) {
        // format transportation milestone
        prevMilestone = this.formatTransportation(
          this.formatedMilestones,
          milestone
        );
        // jump to next index
        return;
      }

      // stacking milestone
      if (
        prevMilestone &&
        prevMilestone.milestoneType === milestone.milestoneType &&
        milestone.milestoneType !== MilestoneTypeEnum.free_text
      ) {
        // add to previous Milestone stack
        prevMilestone.ids.push(milestone.id);
        if (milestone.milestoneType === MilestoneTypeEnum.devanning) {
          const deliveryDates = this.extractDeliveryPlacesDates(milestone);
          prevMilestone.stack = prevMilestone.stack.concat(deliveryDates);
        } else {
          prevMilestone.stack.push(milestone.eventDate);
        }
        // Allows to mark the milestones as not yet done if any of them is not yet done
        if (prevMilestone.doneAt) {
          prevMilestone.doneAt = this.findMilestoneDoneAt(
            milestone,
            prevMilestone.eventName
          );
        }
        // jump to next index
        return;
      }

      prevMilestone = {
        ...milestone,
        ids: [milestone.id],
        stack: [milestone.eventDate],
        eventName: MilestoneDoneDateEnum.doneAt,
        doneAt: this.findMilestoneDoneAt(
          milestone,
          MilestoneDoneDateEnum.doneAt
        ),
        type: getMilestoneType(milestone)
      };

      if (this.isDrayage(milestone)) {
        // add the reservation css class
        prevMilestone.reservationClass = this.getReservationClass(milestone);
      }

      if (milestone.milestoneType === MilestoneTypeEnum.devanning) {
        prevMilestone.eventDate = milestone.devanningDate;
        prevMilestone.stack = this.extractDeliveryPlacesDates(milestone);
      }
      // save prev milestone for next iterration

      // add simple milestone
      this.formatedMilestones.push(prevMilestone);
    });
  }

  private findMilestoneDoneAt(
    milestone: MilestoneFields,
    eventName: MilestoneDoneDateEnum
  ) {
    const doneDate = milestone.doneDates.find(date => {
      return date.eventName === eventName;
    });

    return doneDate ? doneDate.doneAt || null : null;
  }

  private async checkMilestone(milestone: FormatedMilestone) {
    try {
      const newDoneState = milestone.doneAt ? null : new Date();

      await Promise.all(
        milestone.ids.map(id => {
          milestoneService.checkMilestone({
            id,
            done: !!newDoneState,
            eventName: milestone.eventName
          });
        })
      );

      milestone.doneAt = newDoneState;

      this.$store.dispatch('Alert/addAlert', {
        type: 'success',
        message: this.$t('success.updated'),
        time: 1000
      });
    } catch (error) {
      this.$store.dispatch('Alert/addAlert', {
        type: 'error',
        message: this.$t('error.push'),
        time: 2000
      });
    }
  }

  private extractDeliveryPlacesDates(milestone: MilestoneFields): string[] {
    if (
      milestone.devanningDetails &&
      milestone.devanningDetails.container &&
      milestone.devanningDetails.container.deliveryPlaces
    ) {
      return milestone.devanningDetails.container.deliveryPlaces
        .map(dp => {
          return dp.deliveryDate;
        })
        .filter(date => !!date);
    }

    return [];
  }

  private getReservationClass(milestone) {
    if (milestone.details?.drayageReserved !== null) {
      return milestone.details.drayageReserved
        ? ReservationStatusClasses.complete
        : ReservationStatusClasses.none;
    } else {
      const containersLength =
        milestone.devanningDetails?.container?.deliveryPlaces?.length;

      let reserved = 0;
      for (let i = 0; i < containersLength; i++) {
        if (
          milestone.devanningDetails?.container?.deliveryPlaces?.[i]
            ?.drayageReserved
        ) {
          reserved++;
        }
      }
      if (reserved === containersLength) {
        // Complete
        return ReservationStatusClasses.complete;
      } else if (reserved > 0) {
        // Partial
        return ReservationStatusClasses.partial;
      } else {
        // None
        return ReservationStatusClasses.none;
      }
    }
  }

  private isDrayage(milestone) {
    return this.drayageTypes.includes(milestone.milestoneType);
  }
}
